<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useMatterContextModalStore,
    isEntityFieldModified,
    isEntityModified,
} from '../../stores/matterContextModalStore';
import { useResizableColumn } from '../../composables/useResizableColumn';
import { useTableSort } from '../../composables/useTableSort';
import EntityProfileDialog from './EntityProfileDialog.vue';
import ContextMenu, { type ContextMenuItem } from '../common/ContextMenu.vue';
import { SHOW_ENTITY_NAME_PARTS, MATTER_CONTEXT_ROW_EXPANSION } from '../../config/featureFlags';

const { t } = useI18n();
const store = useMatterContextModalStore();

// Display-only sorting on Name / Type (case-insensitive).
const {
    sortedRows: sortedEntities,
    toggle: toggleSort,
    sortState,
    ariaSort,
} = useTableSort(() => store.entities, {
    name: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    entityType: (a, b) =>
        a.entityType.localeCompare(b.entityType, undefined, { sensitivity: 'base' }),
});

const sortIcon = (key: string) => {
    const dir = sortState(key);
    return dir === 'asc' ? 'arrow_upward' : dir === 'desc' ? 'arrow_downward' : 'unfold_more';
};

// Resizable Name column (Context fills the rest). Width is applied as the
// --mc-name-w CSS var on the table; drag the header boundary to adjust the
// Name/Context split. Fixed columns: chevron (32, only when row expansion is
// enabled) + type (140) + action (80).
const {
    tableEl,
    width: nameWidth,
    startResize: startNameResize,
} = useResizableColumn({
    fixedColumnsWidth: (MATTER_CONTEXT_ROW_EXPANSION ? 32 : 0) + 140 + 80,
});

// Accordion: at most one row expanded at a time.
const expandedId = ref<string | null>(null);

const toggleExpand = (id: string) => {
    expandedId.value = expandedId.value === id ? null : id;
};

// Right-side entity profile dialog. `dialogCreateNew` flips it into "new
// entity" mode (a local draft, no store row until confirmed) so adding an
// entity reuses the full edit-field set — type select, validated email — via
// the same dialog. Add is reachable from the empty state and the table's
// "+ Add entity" trigger; both call openCreate.
const dialogVisible = ref(false);
const dialogEntityId = ref<string | null>(null);
const dialogCreateNew = ref(false);

const openDetails = (id: string) => {
    dialogCreateNew.value = false;
    dialogEntityId.value = id;
    expandedId.value = id;
    dialogVisible.value = true;
};

const openCreate = () => {
    dialogCreateNew.value = true;
    dialogEntityId.value = null;
    expandedId.value = null;
    dialogVisible.value = true;
};

// Keep the expanded row underneath the dialog in sync with whichever entity
// the dialog is showing as the user navigates with the arrow buttons.
const onDialogNavigate = (id: string) => {
    dialogEntityId.value = id;
    expandedId.value = id;
};

const closeDetails = () => {
    dialogVisible.value = false;
    dialogCreateNew.value = false;
};

// Row-cell click action, feature-flagged: with row expansion enabled a click
// accordion-expands the inline preview (legacy behavior); with it disabled
// (default) the chevron column and preview don't render at all and a click
// opens the edit dialog directly. expandedId still tracks the dialog's
// current row either way (row highlight + scroll centering).
const onRowClick = (id: string) => {
    if (MATTER_CONTEXT_ROW_EXPANSION) toggleExpand(id);
    else openDetails(id);
};

// Per-row include/exclude switch. ON = included in searches/responses;
// toggling OFF soft-deletes the row (excluded), toggling back ON restores it.
// Blur the input right after toggling so its styled tooltip (shown on
// :focus-within) doesn't stick open once the pointer leaves.
const toggleIncluded = (row: { id: string; deleted?: boolean }, event: Event) => {
    if (row.deleted) store.restoreEntity(row.id);
    else store.deleteEntity(row.id);
    (event.target as HTMLElement | null)?.blur();
};

// Per-row kebab menu (to the right of the switch). Anchored to the icon's
// bottom-right; the shared ContextMenu clamps it into the viewport. Items
// re-trigger existing row actions (open the edit dialog / toggle active).
const rowMenu = ref<{ x: number; y: number; id: string } | null>(null);

const openRowMenu = (event: MouseEvent | KeyboardEvent, id: string) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    rowMenu.value = { x: rect.right, y: rect.bottom + 4, id };
};

const closeRowMenu = () => {
    rowMenu.value = null;
};

const rowMenuItems = computed<ContextMenuItem[]>(() => {
    const id = rowMenu.value?.id;
    const row = id ? store.entities.find((e) => e.id === id) : undefined;
    if (!row) return [];
    return [
        { label: t('matterContext.entities.menuEdit'), icon: 'edit', onSelect: () => openDetails(row.id) },
        row.deleted
            ? {
                  label: t('matterContext.entities.menuMakeActive'),
                  icon: 'check_circle',
                  onSelect: () => store.restoreEntity(row.id),
              }
            : { label: t('matterContext.entities.menuMakeInactive'), icon: 'block', onSelect: () => store.deleteEntity(row.id) },
    ];
});

// Keep the expanded/selected row on screen as the dialog navigates through a
// long list. Centering the row keeps its expanded details below it in view;
// the scroll-margin-top keeps it clear of the sticky header.
watch(expandedId, (id) => {
    if (!id) return;
    nextTick(() => {
        const row = tableEl.value?.querySelector<HTMLElement>(`[data-row-id="${id}"]`);
        row?.scrollIntoView({ block: 'center' });
    });
});
</script>

<template>
    <div v-if="store.entities.length === 0" class="mc-tab-empty">
        <span class="material-icons mc-tab-empty-icon" aria-hidden="true">group_off</span>
        <p class="mc-tab-empty-title">{{ t('matterContext.entities.emptyTitle') }}</p>
        <p class="mc-tab-empty-text">
            {{ t('matterContext.entities.emptyText') }}
        </p>
        <button type="button" class="mc-tab-empty-add" @click="openCreate">
            <span class="material-icons mc-add-icon">add</span>
            <span>{{ t('matterContext.entities.addEntity') }}</span>
        </button>
    </div>

    <template v-else>
        <div class="mc-tab-confirm-bar">
            <span class="mc-tab-confirm-instruction">
                {{ t('matterContext.entities.instruction') }}
            </span>
            <!-- The "Confirm entities" button is commented out (may be revived
                 later). The "Add entity" trigger now lives in its place. -->
            <!-- <button
                type="button"
                class="mc-tab-confirm-btn"
                :class="{ 'mc-tab-confirm-btn-done': store.entitiesConfirmed }"
                :disabled="!store.canConfirmEntities && !store.entitiesConfirmed"
                @click="store.confirmEntities()"
            >
                <span
                    v-if="store.entitiesConfirmed"
                    class="material-icons mc-tab-confirm-icon"
                    aria-hidden="true"
                    >check</span
                >
                {{ store.entitiesConfirmed ? 'Entities confirmed' : 'Confirm entities' }}
            </button> -->
            <button type="button" class="mc-tab-add-btn" @click="openCreate">
                <span class="material-icons mc-add-icon">add</span>
                <span>{{ t('matterContext.entities.addEntity') }}</span>
            </button>
        </div>

        <div
            ref="tableEl"
            class="mc-table"
            :class="{ 'mc-table-no-expand': !MATTER_CONTEXT_ROW_EXPANSION }"
            :style="{ '--mc-name-w': nameWidth + 'px' }"
        >
            <div class="mc-table-header">
                <div v-if="MATTER_CONTEXT_ROW_EXPANSION" class="mc-table-col mc-col-chevron"></div>
                <div
                    class="mc-table-col mc-col-name mc-col-resizable mc-col-sortable"
                    :aria-sort="ariaSort('name')"
                    @click="toggleSort('name')"
                >
                    {{ t('matterContext.entities.colName') }}
                    <span
                        class="material-icons mc-sort-icon"
                        :class="{ 'mc-sort-active': sortState('name') }"
                        aria-hidden="true"
                        >{{ sortIcon('name') }}</span
                    >
                    <span
                        class="mc-col-resize-handle"
                        :title="t('matterContext.common.dragToResize')"
                        @mousedown="startNameResize"
                        @click.stop
                    ></span>
                </div>
                <div class="mc-table-col mc-col-context">{{ t('matterContext.entities.colContext') }}</div>
                <div
                    class="mc-table-col mc-col-type mc-col-sortable"
                    :aria-sort="ariaSort('entityType')"
                    @click="toggleSort('entityType')"
                >
                    {{ t('matterContext.entities.colType') }}
                    <span
                        class="material-icons mc-sort-icon"
                        :class="{ 'mc-sort-active': sortState('entityType') }"
                        aria-hidden="true"
                        >{{ sortIcon('entityType') }}</span
                    >
                </div>
                <div class="mc-table-col mc-col-action"></div>
            </div>

            <template v-for="row in sortedEntities" :key="row.id">
                <div
                    class="mc-table-row"
                    :data-row-id="row.id"
                    :class="{
                        'mc-row-suggestion': row.suggestionStatus === 'pending',
                        'mc-row-accepted': row.suggestionStatus !== 'pending' && !row.deleted,
                        'mc-row-selected': expandedId === row.id,
                        'mc-row-deleted': row.deleted,
                    }"
                >
                    <div v-if="MATTER_CONTEXT_ROW_EXPANSION" class="mc-table-col mc-col-chevron">
                        <span
                            class="material-icons mc-row-chevron mc-row-chevron-active"
                            :class="{ 'mc-row-chevron-open': expandedId === row.id }"
                            :title="expandedId === row.id ? t('matterContext.common.collapse') : t('matterContext.common.expand')"
                            role="button"
                            tabindex="0"
                            @click.stop="toggleExpand(row.id)"
                            @keydown.enter.stop="toggleExpand(row.id)"
                            >expand_more</span
                        >
                    </div>
                    <div
                        class="mc-table-col mc-col-name mc-col-expandable"
                        @click="onRowClick(row.id)"
                    >
                        <span
                            :class="{ 'mc-value-modified': isEntityFieldModified(row, 'name') }"
                            >{{ row.name }}</span
                        >
                    </div>
                    <div
                        class="mc-table-col mc-col-context mc-col-expandable"
                        @click="onRowClick(row.id)"
                    >
                        <span
                            :class="{ 'mc-value-modified': isEntityFieldModified(row, 'context') }"
                            >{{ row.context }}</span
                        >
                    </div>
                    <div
                        class="mc-table-col mc-col-type mc-col-expandable"
                        @click="onRowClick(row.id)"
                    >
                        <span
                            class="mc-entity-pill"
                            :class="{
                                'mc-entity-pill-modified': isEntityFieldModified(row, 'entityType'),
                            }"
                            >{{ row.entityType }}</span
                        >
                    </div>
                    <div class="mc-table-col mc-col-action">
                        <span
                            v-if="expandedId !== row.id && isEntityModified(row)"
                            class="mc-row-mod-dot"
                            :title="t('matterContext.entities.unsavedChanges')"
                        ></span>
                        <label class="mc-switch-wrap" @click.stop>
                            <span class="mc-switch">
                                <input
                                    type="checkbox"
                                    class="mc-switch-input"
                                    :checked="!row.deleted"
                                    @change="toggleIncluded(row, $event)"
                                />
                                <span class="mc-switch-track">
                                    <span class="mc-switch-thumb"></span>
                                </span>
                            </span>
                            <span class="mc-switch-tooltip" role="tooltip">
                                <!-- eslint-disable-next-line vue/no-v-html -- static i18n string with a bolded action word, no user data -->
                                <span v-html="t(row.deleted ? 'matterContext.entities.tooltipExclude' : 'matterContext.entities.tooltipInclude')"></span>
                            </span>
                        </label>
                        <span
                            class="material-icons mc-row-menu"
                            :title="t('matterContext.common.moreActions')"
                            role="button"
                            tabindex="0"
                            @click.stop="openRowMenu($event, row.id)"
                            @keydown.enter.stop="openRowMenu($event, row.id)"
                            >more_vert</span
                        >
                    </div>
                </div>

                <div
                    v-if="MATTER_CONTEXT_ROW_EXPANSION && expandedId === row.id"
                    class="mc-row-expanded"
                >
                    <div class="mc-row-expanded-fields">
                        <div
                            v-if="row.entityType === 'Person' && SHOW_ENTITY_NAME_PARTS"
                            class="mc-row-expanded-field"
                        >
                            <span class="mc-row-expanded-label">{{ t('matterContext.entities.firstName') }}</span>
                            <span
                                class="mc-row-expanded-value"
                                :class="{
                                    'mc-row-expanded-empty': !row.firstName,
                                    'mc-value-modified':
                                        row.firstName && isEntityFieldModified(row, 'firstName'),
                                }"
                            >
                                {{ row.firstName || t('matterContext.common.notSet') }}
                            </span>
                        </div>
                        <div
                            v-if="row.entityType === 'Person' && SHOW_ENTITY_NAME_PARTS"
                            class="mc-row-expanded-field"
                        >
                            <span class="mc-row-expanded-label">{{ t('matterContext.entities.lastName') }}</span>
                            <span
                                class="mc-row-expanded-value"
                                :class="{
                                    'mc-row-expanded-empty': !row.lastName,
                                    'mc-value-modified':
                                        row.lastName && isEntityFieldModified(row, 'lastName'),
                                }"
                            >
                                {{ row.lastName || t('matterContext.common.notSet') }}
                            </span>
                        </div>
                        <div class="mc-row-expanded-field">
                            <span class="mc-row-expanded-label">{{ t('matterContext.entities.colType') }}</span>
                            <span
                                class="mc-entity-pill"
                                :class="{
                                    'mc-entity-pill-modified': isEntityFieldModified(
                                        row,
                                        'entityType',
                                    ),
                                }"
                                >{{ row.entityType }}</span
                            >
                        </div>
                        <div v-if="row.entityType === 'Person'" class="mc-row-expanded-field">
                            <span class="mc-row-expanded-label">{{ t('matterContext.entities.email') }}</span>
                            <span
                                class="mc-row-expanded-value"
                                :class="{
                                    'mc-row-expanded-empty': !row.email,
                                    'mc-value-modified':
                                        row.email && isEntityFieldModified(row, 'email'),
                                }"
                            >
                                {{ row.email || t('matterContext.common.notSet') }}
                            </span>
                        </div>
                    </div>
                    <button type="button" class="mc-view-details-btn" @click="openDetails(row.id)">
                        {{ t('matterContext.common.viewAndEditAllDetails') }}
                    </button>
                </div>
            </template>
        </div>
    </template>

    <EntityProfileDialog
        :visible="dialogVisible"
        :current-id="dialogEntityId"
        :create-new="dialogCreateNew"
        @update:current-id="onDialogNavigate"
        @close="closeDetails"
    />

    <ContextMenu :position="rowMenu" :items="rowMenuItems" @close="closeRowMenu" />
</template>
